import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { Workspace } from '../interfaces/backend/proyectos/project-sections.interface';

export type ProjectStep = 'instrument' | 'record' | 'geography' | 'problems' | 'actors' | 'objectives' | 'formulation';
type Section = 'social' | 'chain' | 'funding';
type Progress = Partial<Record<ProjectStep | Section, boolean>>;

/** Completion comes from saved server data, scoped to a project, never from navigation. */
@Injectable({ providedIn: 'root' })
export class ProjectWorkflowProgressService {
  private readonly http = inject(HttpClient);
  private readonly states = signal<Record<string, Progress>>({});
  private readonly savedStates = signal<Record<string, Progress>>({});
  private readonly requests = new Map<string, number>();
  private readonly base = environment.backendUrl.replace(/\/$/, '') + '/pvt/gb/';

  completed(project: string | null, step: ProjectStep | Section): boolean {
    if (!project) return false;
    const state = this.states()[project] ?? {};
    return step === 'formulation' ? !!(state.social && state.chain && state.funding) : !!state[step];
  }

  saved(project: string | null, section: ProjectStep | Section): boolean {
    if (!project) return false;
    const state = this.savedStates()[project] ?? {};
    return section === 'formulation' ? !!(state.social || state.chain || state.funding) : !!state[section];
  }

  rememberSaved(project: string | null, section: ProjectStep | Section): void {
    if (project) this.savedStates.update(states => ({...states,[project]:{...states[project],[section]:true}}));
  }

  refresh(project: string | null): void {
    if (!project) return;
    const generation = (this.requests.get(project) ?? 0) + 1;
    this.requests.set(project, generation);
    const section = (resource: string) => this.http.get<{data:{completo:boolean;id_registro?:string|null;personas?:{respuesta?:unknown}[]}}>(this.base + resource + '/project/' + project).pipe(catchError(() => of(null)));
    forkJoin({
      workspace: this.http.get<{data:Workspace}>(this.base + 'proyectos/workspace/' + project).pipe(catchError(() => of(null))),
      objectives: section('arboles_objetivos'), social: section('caracterizaciones_sociales_enfoques_genero'),
      chain: section('cadenas_valor'), funding: section('financiaciones_proyectos')
    }).subscribe(result => {
      if (this.requests.get(project) !== generation) return;
      const workspace = result.workspace?.data;
      const prior = this.savedStates()[project] ?? {};
      this.savedStates.update(states => ({...states,[project]:{
        ...prior,
        instrument: !!workspace?.project.id_instrumento,
        record: !!workspace?.project.id_registro,
        problems: !!(prior.problems || workspace?.tree.central || workspace?.tree.nodes?.length),
        actors: !!(prior.actors || workspace?.actors?.length || workspace?.actorFiles?.length),
        objectives: !!(prior.objectives || result.objectives?.data.id_registro || result.objectives?.data.completo),
        social: !!(prior.social || result.social?.data.personas?.some(p=>p.respuesta) || result.social?.data.completo),
        chain: !!(prior.chain || result.chain?.data.id_registro || result.chain?.data.completo),
        funding: !!(prior.funding || result.funding?.data.id_registro || result.funding?.data.completo)
      }}));
      this.states.update(states => ({...states, [project]: {
        instrument: !!workspace?.project.id_instrumento,
        record: !!workspace?.project.id_registro,
        problems: !!workspace?.tree.complete, actors: !!workspace?.actorsComplete,
        objectives: !!result.objectives?.data.completo, social: !!result.social?.data.completo,
        chain: !!result.chain?.data.completo, funding: !!result.funding?.data.completo
      }}));
    });
  }
}
